import { useMemo } from "react";
import { ArrowRight, Clock3, MapPin } from "lucide-react";

function formatDuration(seconds) {
  const total = Math.max(0, Number(seconds) || 0);

  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;

  return `${minutes}m`;
}

export default function StageDurationCard({ stages, fromStageId, toStageId, onFromChange, onToChange, summary, loading }) {
  const fromStage = useMemo(() => stages?.find((stage) => Number(stage.id) === Number(fromStageId)), [stages, fromStageId]);

  const toStage = useMemo(() => stages?.find((stage) => Number(stage.id) === Number(toStageId)), [stages, toStageId]);

  const validToStages = useMemo(() => (stages || []).filter((stage) => !fromStage || Number(stage.position) > Number(fromStage.position)), [stages, fromStage]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="flex flex-wrap items-center justify-center gap-3">
          <div>
            <label className="block text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">From stage</label>
            <select value={fromStageId || ""} onChange={(e) => onFromChange(Number(e.target.value))} className="min-w-[190px] border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-50">
              {(stages || []).map((stage) => (
                <option key={stage.id} value={stage.id}>
                  {stage.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center pt-5 text-gray-300">
            <ArrowRight size={17} />
          </div>

          <div>
            <label className="block text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">To stage</label>
            <select value={toStageId || ""} onChange={(e) => onToChange(Number(e.target.value))} className="min-w-[190px] border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-50">
              {validToStages.map((stage) => (
                <option key={stage.id} value={stage.id}>
                  {stage.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="px-5 py-6">
        <div className="flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-full bg-sky-50 text-sky-500 flex items-center justify-center mb-3">
            <Clock3 size={28} strokeWidth={1.8} />
          </div>

          <div className="text-[11px] font-medium text-gray-400 uppercase tracking-widest mb-1">Average time</div>

          <div className="text-3xl font-bold text-gray-800 leading-tight">{loading ? "…" : formatDuration(summary?.average_seconds)}</div>
        </div>
      </div>

      <div className="px-5 py-3.5 bg-gray-50/70 border-t border-gray-100">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <MapPin size={15} className="text-gray-400" />
            <span>{loading ? "Loading…" : `${summary?.total_addresses || 0} addresses`}</span>
          </div>

          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <span className="text-gray-300">•</span>
            <span className="font-medium text-gray-600">{fromStage?.name || "—"}</span>
            <ArrowRight size={13} className="text-gray-300" />
            <span className="font-medium text-gray-600">{toStage?.name || "—"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
